import { useState, useEffect } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  History,
  User,
  Bot,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  ArrowRight,
} from 'lucide-react'
import { lawyerCasesAPI } from '@/utils/api'

interface ActivityChange {
  field: string
  field_label: string
  old: string | null
  new: string | null
}

interface ActivityUser {
  name: string
  email: string | null
  is_system: boolean
}

interface ActivityEntry {
  id: number
  timestamp: string
  action: '+' | '~' | '-'
  action_label: string
  model: string
  model_label: string
  object_id: number
  object_repr: string
  user: ActivityUser
  changes: ActivityChange[] | null
}

interface ActivityTabProps {
  caseId: number
}

export function ActivityTab({ caseId }: ActivityTabProps) {
  const [activity, setActivity] = useState<ActivityEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await lawyerCasesAPI.getCaseActivity(caseId)
        setActivity(response.data.activity || [])
      } catch (err: any) {
        console.error('Failed to fetch activity:', err)
        setError(err.response?.data?.message || 'Failed to load activity')
      } finally {
        setLoading(false)
      }
    }

    fetchActivity()
  }, [caseId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading activity...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-destructive">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    )
  }

  if (activity.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <History className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No activity yet</p>
        <p className="text-sm">Changes to this case will appear here</p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
        <History className="h-4 w-4" />
        <span>Showing {activity.length} recent changes</span>
      </div>

      <div className="space-y-3">
        {activity.map((entry) => (
          <ActivityEntryCard key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  )
}

function ActivityEntryCard({ entry }: { entry: ActivityEntry }) {
  const timeAgo = formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })

  // Get action icon and color
  const getActionStyle = () => {
    switch (entry.action) {
      case '+':
        return {
          icon: Plus,
          bgColor: 'bg-green-100 dark:bg-green-900/30',
          textColor: 'text-green-600 dark:text-green-400',
        }
      case '-':
        return {
          icon: Trash2,
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          textColor: 'text-red-600 dark:text-red-400',
        }
      default:
        return {
          icon: Pencil,
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          textColor: 'text-blue-600 dark:text-blue-400',
        }
    }
  }

  const style = getActionStyle()
  const ActionIcon = style.icon

  // Check if this entry has important changes to show
  const hasImportantChanges = entry.changes && entry.changes.length > 0

  return (
    <div className="flex gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      {/* Action Icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${style.bgColor} flex items-center justify-center`}>
        <ActionIcon className={`h-4 w-4 ${style.textColor}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* User */}
            <span className="font-medium text-sm">
              {entry.user.is_system ? (
                <span className="flex items-center gap-1">
                  <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                  System
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  {entry.user.name}
                </span>
              )}
            </span>

            {/* Action */}
            <span className="text-sm text-muted-foreground">
              {entry.action_label}
            </span>

            {/* Model */}
            <span className="text-sm font-medium">
              {entry.model_label}
            </span>

            {/* Object representation */}
            {entry.object_repr && (
              <span className="text-sm text-muted-foreground">
                "{entry.object_repr}"
              </span>
            )}
          </div>

          {/* Timestamp */}
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {timeAgo}
          </span>
        </div>

        {/* Changes */}
        {hasImportantChanges && (
          <div className="mt-2 space-y-1">
            {entry.changes!.map((change, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-xs bg-muted/50 rounded px-2 py-1"
              >
                <span className="font-medium text-muted-foreground">
                  {change.field_label}:
                </span>
                {change.old !== null && (
                  <span className="line-through text-muted-foreground">
                    {change.old}
                  </span>
                )}
                {change.old !== null && change.new !== null && (
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                )}
                {change.new !== null && (
                  <span className="font-medium">
                    {change.new}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ActivityTab
